import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { markNotificationRead } from '../../services/notifications';
import { Notification } from '../../types/notification';
import { ChatBubbleLeftRightIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline';

interface GroupedChatNotificationItemProps {
  sender: string;
  notifications: Notification[];
  latest: Notification;
  onMarkAllRead: () => void;
  /** Notify the parent that reads changed, WITHOUT navigating anywhere. */
  onMarked?: () => void;
  onDelete?: (ids: string[]) => void;
  isDeleting?: boolean;
  isEditMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (ids: string[]) => void;
}

const GroupedChatNotificationItem: React.FC<GroupedChatNotificationItemProps> = ({
  sender,
  notifications,
  latest,
  onMarkAllRead,
  onMarked,
  onDelete,
  isDeleting = false,
  isEditMode = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const unreadNotifications = notifications.filter((n) => !n.read);
  const unreadCount = unreadNotifications.length;
  const hasUnread = unreadCount > 0;
  const allIds = notifications.map((n) => n.id);

  const relativeTime = formatDistanceToNow(new Date(latest.created_at), {
    addSuffix: true,
  });

  const handleClick = async () => {
    if (isEditMode) {
      onToggleSelect?.(allIds);
      return;
    }

    if (unreadNotifications.length > 0) {
      await Promise.all(
        unreadNotifications.map((n) =>
          markNotificationRead(n.id).catch(() => {})
        )
      );
    }
    onMarkAllRead();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(allIds);
  };

  const handleCheckboxChange = () => {
    onToggleSelect?.(allIds);
  };

  return (
    <div
      className={`group relative ${isDeleting ? 'opacity-50 pointer-events-none' : ''}`}
      data-testid="grouped-chat-notification-item"
    >
      {isEditMode && (
        <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="w-4 h-4 rounded border-border-primary text-primary-500 focus-visible:ring-primary-500"
            data-testid="grouped-chat-notification-checkbox"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select notifications from ${sender}`}
          />
        </div>
      )}

      <button
        onClick={handleClick}
        className={`
          w-full px-4 py-3 flex items-start gap-3 transition-colors text-left
          hover:bg-background-hover focus:outline-none focus:bg-background-hover
          ${hasUnread ? 'bg-primary-50 dark:bg-primary-900/10' : ''}
          ${isEditMode ? 'pl-10' : ''}
        `}
        data-testid="grouped-chat-notification-button"
      >
        {/* Icon */}
        <div
          className="flex-shrink-0 mt-0.5 text-primary-500 relative"
          data-testid="grouped-chat-notification-icon"
        >
          <ChatBubbleLeftRightIcon className="w-5 h-5" />
          {hasUnread && (
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary-500" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Sender row */}
          <div className="flex items-center gap-2">
            {hasUnread && (
              <span
                className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500"
                data-testid="grouped-chat-notification-unread-dot"
              />
            )}
            <p
              className="text-sm font-semibold text-text-primary truncate"
              data-testid="grouped-chat-notification-sender"
            >
              {sender}
            </p>
            {unreadCount > 0 && (
              <span
                className="flex-shrink-0 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-medium px-1.5 py-0.5 rounded-full"
                data-testid="grouped-chat-notification-badge"
              >
                {unreadCount} new
              </span>
            )}
          </div>

          {/* Latest message preview */}
          <p
            className="text-sm text-text-secondary mt-0.5 line-clamp-1"
            data-testid="grouped-chat-notification-message"
          >
            {latest.message}
          </p>

          {/* Timestamp */}
          <p
            className="text-xs text-text-tertiary mt-0.5"
            data-testid="grouped-chat-notification-timestamp"
          >
            {relativeTime}
          </p>
        </div>
      </button>

      {/* Mark-as-read — clears the whole sender group in place, without
          opening the chat */}
      {!isEditMode && hasUnread && (
        <button
          onClick={async (e) => {
            e.stopPropagation();
            await Promise.all(
              unreadNotifications.map((n) => markNotificationRead(n.id).catch(() => {}))
            );
            onMarked?.();
          }}
          className="absolute top-3 right-10 p-1 rounded text-primary-500 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-500/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-400 transition-colors"
          data-testid="grouped-chat-notification-mark-read"
          aria-label={`Mark messages from ${sender} as read`}
          title="Mark as read"
        >
          <CheckCircleIcon className="w-4 h-4" />
        </button>
      )}

      {/* Delete button — visible on hover, hidden in edit mode */}
      {!isEditMode && onDelete && (
        <button
          onClick={handleDelete}
          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-[150ms] ease-out p-1 rounded hover:bg-background-hover text-text-tertiary hover:text-error-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-error-400"
          data-testid="grouped-chat-notification-delete"
          aria-label={`Delete notifications from ${sender}`}
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default GroupedChatNotificationItem;
